import { Box, Collapse, Divider, Fade, MenuItem, MenuList, SvgIcon, Typography } from '@mui/material';
import { Close as CloseIcon, ViewGrid as GridViewSharpIcon } from '@opengov/react-capital-assets';
import React, { useEffect } from 'react';

import { ClickAwayAndKeydownListener } from '../Components/ClickAwayAndKeydownListener';
import { ProductIcon } from '../Components/icons/products/ProductIcon';
import { useMenuContext } from '../ContextState/useMenuContext';
import { MobileAppSwitcher } from '../ContextSwitcher/AppSwitcher/MobileAppSwitcher';
import { MobileEntitySwitcher } from '../ContextSwitcher/EntitySwitcher/MobileEntitySwitcher';

import { Error } from '../ContextSwitcher/ErrorState';

import { DATA_TEST, getDynamicDataTestId } from '../enums';
import { IContextSwitcherData, IEntityDetails, IProductDetails, PRODUCT_ICONS, PRODUCT_KEY } from '../types';

import { shouldEnableSubmenu } from '../TopLevelNavigation/utils';
import { MobileMenuContent } from './Components/MobileMenuContent';
import { MobileKeyboardNavContextProps, useMobileKeyboardNavContext } from './Context/MobileKeyboardNavContext';

const renderAppNameMenuItemContent = (
  appName: string,
  activeProductKey: PRODUCT_KEY,
  showContextSwitcher: boolean,
  hideApplicationSwitcher: boolean
) => {
  const activeProductIcon = PRODUCT_ICONS[activeProductKey];
  return (
    <Box
      display="flex"
      width="100%"
      px={2}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 40
      }}
    >
      {activeProductIcon && (
        <SvgIcon
          viewBox="0 0 16 16"
          data-test={getDynamicDataTestId(DATA_TEST.mobile_active_product_icon, activeProductKey)}
          sx={{
            pt: '1px',
            svg: {
              fill: 'currentColor'
            },
          color: 'primary.main',
            fontSize: '16px'
          }}
        >
          <ProductIcon icon={activeProductIcon} />
        </SvgIcon>
      )}
      <Typography
        variant="h4"
        component="span"
        sx={{
          fontWeight: 600,
          color: 'primary.main'
        }}
        flexGrow={1}
      >
        {appName}
      </Typography>
      {!hideApplicationSwitcher && (
        <Box sx={{ position: 'relative', width: 24, height: 24 }}>
          <Fade in={!showContextSwitcher}>
            <Box>
              <GridViewSharpIcon
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'text.secondary',
                  fontSize: '16px'
                }}
              />
            </Box>
          </Fade>
          <Fade in={showContextSwitcher}>
            <Box>
              <CloseIcon
                sx={{
                  position: 'absolute',
                  color: 'text.secondary',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '16px'
                }}
              />
            </Box>
          </Fade>
        </Box>
      )}
    </Box>
  );
};

interface IMobileMenuProps {
  appName: string;
  hamburgerRef: React.RefObject<HTMLButtonElement>;
  contextSwitcherData?: IContextSwitcherData;
  hideContextSwitcher?: boolean;
  showOnlySwitchMenu?: boolean;
  loadingError?: boolean;
  handleClose(): void;
}

export const MobileMenu: React.FC<IMobileMenuProps> = ({
  handleClose,
  appName,
  contextSwitcherData,
  loadingError,
  showOnlySwitchMenu = false
}) => {
  const { menuItems, setCurrentLevelOneItem } = useMenuContext();
  const { firstMobileMenuItemRef, setFirstMobileMenuItemIsContextSwitcher, clearFocusNextStates } =
    useMobileKeyboardNavContext() as MobileKeyboardNavContextProps;
  const resetMenuContext = () => {
    handleClose();
    setCurrentLevelOneItem(null);
  };
  const firstAppSwitcherItemRef = React.useRef<HTMLLIElement>(null);
  const menuListRef = React.useRef<HTMLUListElement>(null);
  const [showContextSwitcher, setShowContextSwitcher] = React.useState(false);
  const [showSwitchMenu, setShowSwitchMenu] = React.useState(false);
  const entitySwitcherNotClickable = !contextSwitcherData?.entitledEntities?.some(
    (entity) => entity.id !== contextSwitcherData.activeEntity.id
  );
  const hideApplicationSwitcher = !!contextSwitcherData?.forcedDefaultApplication;

  // Always show the context switcher in mobile view, even when there is only one entity and one non-admin app
  // This overrides forceContextSwitcherVisibility to true for mobile while preserving parity with desktop behavior
  const enableContextSwitcher = shouldEnableSubmenu({ ...contextSwitcherData, forceContextSwitcherVisibility: true });
  useEffect(() => {
    setFirstMobileMenuItemIsContextSwitcher(enableContextSwitcher);
  }, [enableContextSwitcher]);

  return (
    <MenuList
      data-test={DATA_TEST.mobile_menu_container}
      role="menu"
      ref={menuListRef}
      sx={{
        py: 0,
        '.MuiMenuItem-root': {
          minHeight: 40
        },
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: 1,
        ...(!enableContextSwitcher && { paddingTop: 1 })
      }}
    >
      {loadingError ? (
        <Error styleOverrides={{ maxWidth: undefined }} />
      ) : (
        enableContextSwitcher && [
          <MobileEntitySwitcher
            key="mobile-entity-switcher"
            activeEntity={contextSwitcherData?.activeEntity as IEntityDetails}
            entitledEntities={contextSwitcherData?.entitledEntities}
          />,
          hideApplicationSwitcher ? (
            <MenuItem
              key="mobile-app-name"
              data-test={DATA_TEST.mobile_app_name}
              sx={{
                display: 'flex',
                p: 0,
                pointerEvents: 'none'
              }}
            >
              {renderAppNameMenuItemContent(
                appName,
                contextSwitcherData?.activeProductKey,
                showContextSwitcher,
                hideApplicationSwitcher
              )}
            </MenuItem>
          ) : (
            <ClickAwayAndKeydownListener
              handleTrigger={() => {
                if (showContextSwitcher) setShowContextSwitcher(false);
              }}
              triggerKeys={['Escape']}
              key="mobile-app-switcher"
            >
              <MenuItem
                aria-label="Switch application"
                aria-expanded={showContextSwitcher}
                data-test={DATA_TEST.mobile_app_switcher_button}
                ref={entitySwitcherNotClickable ? firstMobileMenuItemRef : null}
                autoFocus={entitySwitcherNotClickable}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && showContextSwitcher) {
                    // Close just the app switcher on escape key when focus is on the app switcher
                    e.preventDefault();
                    e.stopPropagation();
                    setShowContextSwitcher(false);
                    e.currentTarget.focus();
                  }
                  // Arrow Navigation while app switcher is open/expanded
                  if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && showContextSwitcher) {
                    // If ArrowUp key is pressed on the first item in the app switcher
                    if (e.key === 'ArrowUp' && e.target === firstAppSwitcherItemRef?.current) {
                      // Move focus to app switcher button
                      e.currentTarget.focus();
                    }
                    // Since this is a MenuList inside a MenuList, prevent from bubbling up to the parent MenuList
                    e.preventDefault();
                    e.stopPropagation();
                    // If ArrowDown key is pressed on the app switcher button
                    if (e.key === 'ArrowDown' && firstAppSwitcherItemRef?.current && e.target === e.currentTarget) {
                      firstAppSwitcherItemRef.current.focus();
                    }
                  }
                  // When app switcher is open/expanded and target is not the app switcher button
                  // In other words, target is some item in the app switcher
                  if (showContextSwitcher && e.target !== e.currentTarget) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onClick={() => setShowContextSwitcher((s) => !s)}
                sx={{
                  display: 'flex',
                  p: 0,
                  ':focus': {
                    outlineOffset: 0
                  }
                }}
              >
                <Box width="100%" display="flex" flexDirection="column">
                  {renderAppNameMenuItemContent(
                    appName,
                    contextSwitcherData?.activeProductKey as PRODUCT_KEY,
                    showContextSwitcher,
                    hideApplicationSwitcher
                  )}
                  <Collapse
                    data-test={DATA_TEST.mobile_app_switcher_collapse}
                    in={showContextSwitcher}
                    onEntered={() => {
                      firstAppSwitcherItemRef?.current?.focus();
                      clearFocusNextStates();
                    }}
                  >
                    <MobileAppSwitcher
                      handleClose={() => {}}
                      entitledApplications={
                        contextSwitcherData?.activeEntity?.entitledApplications as IProductDetails[]
                      }
                      activeProductKey={contextSwitcherData?.activeProductKey as PRODUCT_KEY}
                      firstAppSwitcherItemRef={firstAppSwitcherItemRef as React.MutableRefObject<HTMLLIElement>}
                    />
                  </Collapse>
                </Box>
              </MenuItem>
            </ClickAwayAndKeydownListener>
          ),
          menuItems.length > 0 && <Divider sx={{ mb: 1, mt: '0 !important' }} key="mobile-menu-divider" />
        ]
      )}
      {/* When context switcher is expanded (showContextSwitcher), apply special styling to disable menu items.
      Although menu items should be direct children of MenuList for accessibility and keyboard navigation,
      an outer Box component is acceptable here as items aren't interactive when context switcher is open. */}
      {showContextSwitcher ? (
        <Box
          sx={{
            position: 'relative',
            ...(showContextSwitcher && {
              ':before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                backgroundColor: 'background.paper',
                opacity: 0.6,
                zIndex: 1
              }
            })
          }}
        >
          <MobileMenuContent
            showOnlySwitchMenu={showOnlySwitchMenu}
            resetMenuContext={resetMenuContext}
            menuListRef={menuListRef}
            autoFocusFirstItem={!enableContextSwitcher || (hideApplicationSwitcher && entitySwitcherNotClickable)}
            showSwitchMenu={showSwitchMenu}
            setShowSwitchMenu={setShowSwitchMenu}
          />
        </Box>
      ) : (
        <MobileMenuContent
          showOnlySwitchMenu={showOnlySwitchMenu}
          resetMenuContext={resetMenuContext}
          menuListRef={menuListRef}
          autoFocusFirstItem={!enableContextSwitcher || (hideApplicationSwitcher && entitySwitcherNotClickable)}
          showSwitchMenu={showSwitchMenu}
          setShowSwitchMenu={setShowSwitchMenu}
        />
      )}
    </MenuList>
  );
};
